
import { useDispatch, useSelector } from "react-redux";
import { createFailureAction, createSetDeviceVitalsInfo, createSetSessionDetailsAction, createSetUpdateComponentVersionsAction, createSetViewAction, createUpdateProgressAction } from "state/reducer";
import { getDeviceIdFromStorage, getDeviceType,isPageReloaded,randomstring,storeDeviceId} from "../util";
import { SessionDetails,IpcMessageType,IADPState, ExtensionMessage, NetworkConnectionState, UpdateStates } from "../Types/AppStateTypes";
import { client } from "../Types/IPCTypes";
import { v4 as uuidv4 } from "uuid";
declare global {
  interface Window {
    adpElectronBridge?: {
      greet: (arg0: client) => Promise<SessionDetails>;
      isUpdateAvailable: () => Promise<IpcMessageType>;
      postChromeAppState:(state:IADPState)=>void;
      enterKioskMode:()=>void;
      exitKioskMode:()=>void;
      listenOnNetworkStatusChanges:()=>void;
      sendSetupCode: (setupCode:string) => void;
      sendDeliveryMode: (deliveryMode:string) => void;
      getReleaseVersion: () => string;
    };
  }
}
export const isElectronBridgeAvailable = () => {
  return window["adpElectronBridge"];
};
export const postMessageToAdpBridge = (message: ExtensionMessage) => {
  window.postMessage(message);
};
export function useIPCBridge(
) {
  const dispatch = useDispatch();
  const state = useSelector((state:{rootState:IADPState})=>state.rootState)
  const win: Window = window;
   
  /**
   * send greeting to ADP electron main process or ADP chrome extension.
   * If none is available, application is runnng in the browser without any backend support;
   * >>> Communication with Electron is established via preload.js which is part of the adp electron app and is injected
   * on the app startup. All methods are available at Window scope
   * >>> Communcatioon with Chrome Extension is established via Events that are emitted to the Window's context
   */
  async function sendGreeting() {
    //send greeting to Electron and obtain session and version details from the main process, if electron is not available read local manifest file
    const hardwareInfo=()=>{
      const promise =  new Promise<{hardwareInfo:any | string,systemInfo: any}>((resolve) => {
        const eventTimeoutId = setTimeout(() => {
          resolve({hardwareInfo:"did not resolve",systemInfo: "did not resolve"});
        }, 5000);
          const handler =(event: any)=>{
            clearTimeout(eventTimeoutId);
            resolve(event.detail?.data);
              window.removeEventListener('HardwareInfoEvent',handler);
          }
            window.addEventListener('HardwareInfoEvent', handler);
        });
      
        return promise;
    }
    if (isElectronBridgeAvailable()) {
      console.log("[ADPClient][IPCBridge] App is running in Electron, send greeting to main process");
      return win["adpElectronBridge"]?.greet({
        clientCode:state?.appState.appCode as string,
        clientVersion:state?.appState.appVersion as string,
        clientBuildDate: state?.appState.appBuildDate as string,
        clientURL:window.location.href
      }).then(async (details: any) => {
        dispatch(createSetDeviceVitalsInfo(details.deviceValidationResults))
        const storedDeviceId = await getDeviceIdFromStorage();
        let sessionDetails: SessionDetails = {
          ...details,
          deviceId:details?.deviceId ? details?.deviceId.replace(/[^a-z0-9]/gi, '') : storedDeviceId ? storedDeviceId : `SD${randomstring(5).toUpperCase()}`,
          deviceType:details?.deviceType,//device type will be set by electron, no need to modify
          networkConnectionState: window.navigator.onLine ? "Connected" : "N/A" as NetworkConnectionState,
          networkConnectionDetails: details?.networkConnectionState ,
          isPageReloaded: isPageReloaded(),
          runtimePlatform: {
            platform: "Electron",
            version: details?.version,
            userAgent: navigator.userAgent,
          },
        };
        storeDeviceId(sessionDetails.deviceId);
        const nscEvent = new CustomEvent('network-state-changed-init', {
          detail: sessionDetails.networkConnectionState,
        });
        window.dispatchEvent(nscEvent);
        try {
          console.log("[ADPClient][IPCBridge] send message to extenstion for hardware info")
          hardwareInfo().then((info)=>{
            console.log("hardwareInfo from extenstion",info)
          })
        }
        catch(e){
          console.warn("error getting hardwareInfo from extenstion",e);
        }
        //check date time
          dispatch(createSetSessionDetailsAction(sessionDetails));
        //subscribe to network events
        win["adpElectronBridge"]?.listenOnNetworkStatusChanges();
        //send greeting to the Chrome Extension loaded with Electron 
        console.log("[ADPClient][IPCBridge] send greeting to ADP extension...");
        postMessageToAdpBridge({
          type: "Greeting",
          data: { client: "ADP", appLaunchId: sessionDetails.appLaunchId},
        });
        const handler = (event: any,) => {
          console.log(
            "[ADPClient][IPCBridge] Chrome Extension Version:",
            event?.detail?.data?.chromeExtensionVersion
          );
          dispatch(createSetUpdateComponentVersionsAction({name: "ADP-EXTENSION", version: event?.detail?.data?.chromeExtensionVersion }));
            window.removeEventListener("GreetingEvent", handler);
      }
      return window.addEventListener("GreetingEvent", handler);
      });
    
    } else {
      console.log("[ADPClient][IPCBridge] App is running in Browser, send greeting to chrome extension");
      //send greeting to chrome extension to check if Chrome extension is available
      const appLaunchId = uuidv4();
      const storedDeviceId = await getDeviceIdFromStorage();
      const deviceId = storedDeviceId ? storedDeviceId :`SD${randomstring(5).toUpperCase()}`
      storeDeviceId(deviceId);
      let sessionDetails: SessionDetails = {
        appLaunchId: appLaunchId,
        deviceId: deviceId, 
        deviceModel: navigator.userAgent, 
        deviceType: getDeviceType(),
        networkConnectionState:window.navigator.onLine ? "Connected" : "N/A",
        isPageReloaded: isPageReloaded(),
        runtimePlatform: {
          platform: "Browser",
          version: state.appState.appVersion,
          userAgent: navigator.userAgent,
        },
      };
      //TODO - find a better way to check if extension is installed, e.g inject extension version
      const timeoutId = setTimeout(async() => {
        console.log("[ADPClient][IPCBridge] extension is not available");
        console.warn('[ADPClient][IPCBridge] app is running with limited functionalites, consider adding ADP Chrome extension for full experience')
        dispatch(createSetSessionDetailsAction(sessionDetails));
        return;
      }, 5000);
      console.log("[ADPClient][IPCBridge] send greeting to ADP extension...");
      postMessageToAdpBridge({
        type: "Greeting",
        data: { client: "ADP", appLaunchId: appLaunchId },
      });
      //listen for event from ChrEx
      const handler = (event: any) => {
        console.log(
          "[ADPClient][IPCBridge] Chrome Extension Version:",
          event?.detail?.data?.chromeExtensionVersion
        );
        dispatch(createSetUpdateComponentVersionsAction({name: "ADP-EXTENSION", version: event?.detail?.data?.chromeExtensionVersion }));
        clearTimeout(timeoutId);
          window.removeEventListener("GreetingEvent", handler);
          const platformInfo=()=>{
           
            const promise =  new Promise<{os: string,arch: string,nacl_arch:string}>((resolve) => {
                const handler =(event: any)=>{
                  resolve(event.detail?.data);
                  window.removeEventListener('PlatformInfoEvent',handler);
                }
                  window.addEventListener('PlatformInfoEvent', handler);
              })
              return promise;
            };
          const  deviceDetails=()=>{
              const promise =  new Promise<string>((resolve) => {
                const eventTimeoutId = setTimeout(() => {
                  resolve("DeviceDetailsEvent did not resolve");
                }, 5000);
                  const handler =(event: any)=>{
                    clearTimeout(eventTimeoutId);
                    resolve(event.detail?.data);
                    window.removeEventListener('DeviceDetailsEvent',handler);
                  }
                    window.addEventListener('DeviceDetailsEvent', handler);
                })
                return promise;
            }
           const networkDetails=()=>{
              const promise =  new Promise<{macAddress: string, ipv4:string,ipv6:string} | string>((resolve) => {
                const eventTimeoutId = setTimeout(() => {
                  resolve("NetworkDetailsEvent did not resolve");
                }, 5000);
                  const handler =(event: any)=>{
                    clearTimeout(eventTimeoutId);
                    resolve(event.detail?.data);
                      window.removeEventListener('NetworkDetailsEvent',handler);
                  }
                    window.addEventListener('NetworkDetailsEvent', handler);
                });
              
                return promise;
            }
            Promise.all([platformInfo(),deviceDetails(),networkDetails(),hardwareInfo()]).then(async (values)=>{
              console.log(values);
              const storedDeviceId = await getDeviceIdFromStorage();
              const deviceId = values[1]?.indexOf("is not available") > -1 ? storedDeviceId ? storedDeviceId : `SD${randomstring(5).toUpperCase()}` : values[1].replace(/[^a-z0-9]/gi, '');
              storeDeviceId(deviceId);
              const address =  typeof values[2] === "string" ? values[2] : values[2]?.ipv4
              const network = window.navigator.onLine ? "Connected" : "N/A";
               const deviceModel = typeof values[3] === "string" || !values[3]?.hardwareInfo ? navigator.userAgent : values[3]?.hardwareInfo;
               const systemInfo = values[3]?.systemInfo;
               let sessionDetails: SessionDetails = {
                deviceId: deviceId,
                deviceModel:deviceModel,
                deviceType: getDeviceType(),
                appLaunchId: appLaunchId,
                isPageReloaded: isPageReloaded(),
                networkConnectionState:network,
                networkConnectionDetails: {connectionType:network,address: address},
                systemInfo: systemInfo,
                runtimePlatform: {
                  platform: "ChromeWithExtension",
                  version: event?.detail?.data?.chromeExtensionVersion,
                  userAgent: navigator.userAgent,
                },
              }; 
              dispatch(createSetSessionDetailsAction(sessionDetails));
              const nscEvent = new CustomEvent('network-state-changed', {
                detail: network,
              });
              window.dispatchEvent(nscEvent);
            });
           
           postMessageToAdpBridge({type:"NetworkDetails"}); 
           postMessageToAdpBridge({type:"DeviceDetails"}); 
           postMessageToAdpBridge({type:"PlatformInfo"}); 
           postMessageToAdpBridge({type:"HardwareInfo"});  
           return;
          }
             
      return window.addEventListener("GreetingEvent", handler);
    }
  }
  async function getReleaseVersion(){
    if (isElectronBridgeAvailable()) {
      const releaseVersion = win["adpElectronBridge"]?.getReleaseVersion();
      return releaseVersion
    }
    else{
      return undefined
    }
  }
  function checkForUpdates() {
    const onUpdateEvents = (e:any) => {
      console.table(e.detail);
      if(e.detail?.type === "update-fast-forward"){
        dispatch(createSetViewAction("ModeSelectionsView"));
      }
      else {
        dispatch(createUpdateProgressAction(e.detail));
      }
    };
    window.addEventListener("update-progress",onUpdateEvents);
    win["adpElectronBridge"]
      ?.isUpdateAvailable()
      .then((data: IpcMessageType) => {
        console.log("[ADPClient][IPCBridge]");
        console.table(data);
        const updateType:UpdateStates = data.type;
        window.removeEventListener("update-progress",onUpdateEvents);
        if (updateType === "update-not-available") {
          dispatch(createUpdateProgressAction(data));
          setTimeout(()=>{
            dispatch(createSetViewAction("ModeSelectionsView"));
         },5000);
        } else if (updateType === "update-error") {
          dispatch(createFailureAction("UpdateError"));
        }
        else if (updateType === "update-complete") {
          setTimeout(()=>{
            dispatch(createUpdateProgressAction(data));
         },1000);
        }
        else if(updateType === "update-fast-forward"){
          dispatch(createSetViewAction("ModeSelectionsView"));
        }
        else if(updateType === "update-is-in-progress"){
          dispatch(createSetViewAction("UpdaterView"));
        }
      })
      .catch((e) => {
        console.error("[ADPClient][IPCBridge]", e);
        dispatch(createFailureAction("UpdateError"));
        window.removeEventListener("update-progress",onUpdateEvents);
      });
  }
  function saveADPChromeAppState(state:IADPState){
    if (isElectronBridgeAvailable()) {
      win["adpElectronBridge"]?.postChromeAppState(state)
    }
    else {
      postMessageToAdpBridge({
        type: "PostChromeAppState"
      });
      
    }
  };
  function sendSetupCode (setupCode:string){
    win["adpElectronBridge"]?.sendSetupCode(setupCode);
  }
  function sendDeliveryMode (deliveryMode:string){
    win["adpElectronBridge"]?.sendDeliveryMode(deliveryMode);
  }
  function setKioskMode(){
    try {
      console.log("[ADPClient][IPCBridge] setKioskMode called");
      win["adpElectronBridge"]?.enterKioskMode();
    }
    catch(e){
      console.warn("setKioskMode",e)
    }
  }
  function exitKioskMode(){
    try {
      console.log("[ADPClient][IPCBridge] exitKioskMode called");
      win["adpElectronBridge"]?.exitKioskMode();
    }
    catch(e){
      console.warn("exitKioskMode",e)
    }
   
  }
  return { setKioskMode,exitKioskMode,sendGreeting, checkForUpdates,saveADPChromeAppState,sendSetupCode,sendDeliveryMode,getReleaseVersion};
};

